import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const JobDetailsModal = ({ job, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: job?.title || '',
    description: job?.description || '',
    priority: job?.priority || 'medium',
    trade: job?.trade || '',
    contractor: job?.contractor || '',
    estimatedCost: job?.estimatedCost || '',
    notes: job?.notes || ''
  });

  if (!isOpen || !job) return null;

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({ ...job, ...formData });
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const priorityOptions = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const tradeOptions = [
    { value: 'Plumbing', label: 'Plumbing' },
    { value: 'Electrical', label: 'Electrical' },
    { value: 'Heating', label: 'Heating' },
    { value: 'Carpentry', label: 'Carpentry' },
    { value: 'Painting', label: 'Painting' },
    { value: 'Roofing', label: 'Roofing' },
    { value: 'Glazing', label: 'Glazing' },
    { value: 'General', label: 'General Maintenance' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-3xl" onClick={(e) => e?.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground font-heading">Job Details</h2>
            <p className="text-sm text-muted-foreground font-data">{job?.id}</p>
          </div>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
            aria-label="Close"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
            <div className="md:col-span-2">
              <Input
                label="Job Title"
                type="text"
                value={formData?.title}
                onChange={(e) => handleChange('title', e?.target?.value)}
                required
              />
            </div>

            <div className="md:col-span-2">
              <Input
                label="Description"
                type="text"
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                required
              />
            </div>

            <Select
              label="Priority"
              options={priorityOptions}
              value={formData?.priority}
              onChange={(value) => handleChange('priority', value)}
              required
            />

            <Select
              label="Trade"
              options={tradeOptions}
              value={formData?.trade}
              onChange={(value) => handleChange('trade', value)}
              required
            />

            <div className="md:col-span-2">
              <div className="bg-input rounded-lg p-4 border border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="MapPin" size={18} color="var(--color-accent)" />
                  <p className="text-sm font-medium text-foreground">Property Details</p>
                </div>
                <p className="text-sm text-foreground mb-1">{job?.propertyAddress}</p>
                <p className="text-xs text-muted-foreground font-data">{job?.propertyId}</p>
              </div>
            </div>

            {job?.contractor && (
              <div className="md:col-span-2">
                <div className="bg-input rounded-lg p-4 border border-border">
                  <div className="flex items-center gap-3">
                    <Image 
                      src={job?.contractorAvatar} 
                      alt={job?.contractorAvatarAlt}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{job?.contractor}</p>
                      <p className="text-xs text-muted-foreground">{job?.trade}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Icon name="Star" size={14} color="var(--color-warning)" />
                        <span className="text-xs text-muted-foreground font-data">{job?.contractorRating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Input
              label="Estimated Cost (£)"
              type="number"
              value={formData?.estimatedCost}
              onChange={(e) => handleChange('estimatedCost', e?.target?.value)}
              placeholder="0.00"
            />

            <div className="md:col-span-2">
              <Input
                label="Notes"
                type="text"
                value={formData?.notes}
                onChange={(e) => handleChange('notes', e?.target?.value)}
                placeholder="Add any additional notes..."
              />
            </div>
          </div>

          <div className="flex gap-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName="Check"
              iconPosition="left"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobDetailsModal;